import {
  ComponentProps,
  FocusEventHandler,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { mapDateToUI, mapToDatepickerObject } from '@bridebook/toolbox';
import { IDatePickerUI } from '@bridebook/toolbox/src/datepicker/types';
import { Market } from '@bridebook/toolbox/src/gazetteer';
import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';
import getDatepickerTitle from '@bridebook/web/lib/datepicker/utils/get-datepicker-title';
import isDateExact from '@bridebook/web/lib/weddings/utils/is-date-exact';
import { cn, Text } from '@bridebook/aura';
import { componentStyles } from './date-input-native.style';
import { InputV2 } from '@bridebook/ui';

interface IProps extends Omit<ComponentProps<typeof InputV2>, 'type' | 'value'> {
  market?: Market;
  value?: string; // RHF value (ISO 'YYYY-MM-DD' or '')
}

const formatISOForDisplay = (iso: string, locale: string) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return '';
  const [y, m, d] = iso.split('-').map(Number);
  try {
    return new Intl.DateTimeFormat(locale || 'en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    }).format(new Date(y, m - 1, d));
  } catch {
    return iso;
  }
};

const DateInputNative = ({
  value = '',
  id,
  onFocus,
  onBlur,
  market: propsMarket,
  className,
  // prevent consumers accidentally making it uncontrolled mid-flight
  defaultValue: _ignored,
  ...props
}: IProps) => {
  const reactId = useId();
  const forId = id ?? reactId;

  const marketFromHook = useMarket();
  const market = propsMarket ?? marketFromHook;

  const displayValue = useMemo(() => {
    const safe = formatISOForDisplay(value, (market as any)?.locale ?? 'en-GB');
    if (safe) return safe;
    const dp = mapToDatepickerObject(value);
    if (!isDateExact(dp)) return '';
    const ui = mapDateToUI(dp) as IDatePickerUI;
    return getDatepickerTitle(ui, false, false, market);
  }, [value, market]);

  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFocus: FocusEventHandler<HTMLInputElement> = (e) => {
    setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur: FocusEventHandler<HTMLInputElement> = (e) => {
    setIsFocused(false);
    onBlur?.(e);
  };

  useEffect(() => {
    if (isFocused) inputRef.current?.focus();
  }, [isFocused]);

  return (
    <div className={cn('relative w-full', className)}>
      <InputV2
        {...props}
        inputStyle={componentStyles.dateInput}
        value={value}
        type="date"
        id={forId}
        ref={inputRef}
        onFocus={handleFocus}
        onBlur={handleBlur}
        max="9999-12-31"
        className={cn('w-full', !isFocused && 'text-transparent caret-transparent')}
        beforeInput={
          !isFocused ? (
            // Overlay label when blurred, displaying placeholder when there's no value
            <Text
              className={cn(
                'pointer-events-none absolute inset-0 flex h-[24px] items-center',
                !displayValue && 'text-space-40',
              )}
              aria-hidden="true">
              {displayValue || props.placeholder}
            </Text>
          ) : undefined
        }
      />
    </div>
  );
};

export default DateInputNative;
